/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useEffect, useState } from 'react';
import {
  LenderTable,
  Text,
  BrandColors,
  Header,
  MaterialIcon,
  LoneIconButton,
  Modal,
  ActionButton,
} from '@blend/enterprise-components';
import { css, OpenCSSProperties, StyleSheet } from 'aphrodite';
import { orderBy } from 'lodash';
import * as Sentry from '@sentry/browser';

import { updateUserPreference } from 'blb-api/users';
import { Report } from 'blb-types/internal/api/report';
import { Accordion } from 'blb-components/AuthenticatedApp/Accordion/Accordion';

import { ModalContent } from '../ModalContent';
import { useToasts } from '../AuthenticatedApp/Toast';

const styles = StyleSheet.create({
  activeStatus: {
    ':before': {
      color: '#39BF7C',
    },
  },
  buttonRow: {
    display: 'flex',
    gap: '0.5rem',
    justifyContent: 'flex-end',
    marginTop: '1rem',
  },
  deactivateBtn: {
    bottom: '0.25rem',
    left: '0.5rem',
  },
  headerStyle: {
    backgroundColor: BrandColors.WHITE,
    height: '54px',
  },
  headerTextWrapper: {
    height: '16px',
    overflow: 'hidden',
    paddingRight: '5px',
    position: 'relative',
    top: '3px',
  },
  inactiveStatus: {
    ':before': {
      color: '#CA4654',
    },
  },
  lenderTableWrapper: {
    display: 'flex',
    overflowX: 'scroll',
    position: 'absolute',
    // page header height + date header height
    top: '140px',
  },
  numberStyle: {
    alignItems: 'center',
    backgroundColor: BrandColors.CLOUD_70,
    borderRadius: '3px',
    color: BrandColors.NIGHT_60,
    display: 'inline-block',
    height: '20px',
    padding: '0px 5px',
  },
  sortableHeader: {
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'row',
  },
  statusIndicator: {
    ':before': {
      borderRadius: '50%',
      content: "'●'",
      display: 'inline-block',
      height: '10px',
      marginRight: '5px',
      width: '10px',
    },
  },
});

export namespace TemplateSortableTable {
  export interface Props {
    headers: Report.AssignmentReportHeader[];
    id: string;
    maxAccordionTitleCharacterLength?: number;
    refresh: () => Promise<void>;
    rowStyle?: OpenCSSProperties;
    rows: Report.AssignmentReportRecord[];
  }

  export interface State {
    currentSortingColumn: Report.AssignmentReportHeaderIds | null;
    sortOrder: 'asc' | 'desc';
  }
}

export const TemplateSortableTable: React.FC<TemplateSortableTable.Props> = ({
  headers,
  id,
  refresh,
  rowStyle,
  rows,
  maxAccordionTitleCharacterLength = 48,
}) => {
  const [state, setState] = useState<TemplateSortableTable.State>({
    currentSortingColumn: null,
    sortOrder: 'asc',
  });
  // columnWidths will get populated when setFormattedRecords is executed
  const columnWidths: number[] = [];
  interface FormattedRecords {
    formattedHeaders: React.ReactNode[];
    formattedRows: React.ReactNode[][];
  }
  const [formattedRecords, setFormattedRecords] = useState<FormattedRecords | null>(null);

  useEffect(() => {
    setFormattedRecords({
      formattedHeaders: renderHeader(),
      formattedRows: renderRows(),
    });
  }, [state]);

  const sortByColumnIndex = (colId: Report.AssignmentReportHeaderIds): void => {
    const sortOrder = (() => {
      if (state.currentSortingColumn === colId) {
        return state.sortOrder === 'desc' ? 'asc' : 'desc';
      }
      return 'asc';
    })();
    setState({ currentSortingColumn: colId, sortOrder });
  };

  const getSortingArrow = (): React.ReactNode => {
    const iconType = state.sortOrder === 'asc' ? 'arrow_downward' : 'arrow_upward';
    const color = BrandColors.NIGHT_90;
    return <MaterialIcon color={color} size={14} type={iconType} />;
  };

  /**
   * Function responsible for populating the headers of the table and also sets the
   * col width state
   */
  const renderHeader = (): React.ReactNode[] => {
    const derivedColWidth: number[] = [];
    // Formula to calculate width: width/totalWidth * 100%
    const derivedHeaders = headers.map((header: Report.AssignmentReportHeader) => {
      const { sortable, size, id: headerId } = header;
      derivedColWidth.push(size);
      const headerComponent = (
        <div className={css(styles.headerTextWrapper)}>
          <Header colorName={'NIGHT_90'} ellipsis fontWeight={'MEDIUM'} size={6}>
            {header.text}
          </Header>
        </div>
      );

      return (
        <div
          className={css(sortable && styles.sortableHeader)}
          id={`it-${headerId}-header`}
          key={`${headerId}-header-key`}
          onClick={sortable ? () => sortByColumnIndex(headerId) : undefined}
        >
          {headerComponent}
          {state.currentSortingColumn === headerId ? getSortingArrow() : ''}
        </div>
      );
    });
    return derivedHeaders;
  };

  interface PrincipalToBeDeactivated {
    id: string;
    name: string;
  }
  const [principalToBeDeactivated, setPrincipalToBeDeactivated] = useState<PrincipalToBeDeactivated | undefined>(
    undefined,
  );
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState<boolean>(false);
  const [isDeactivationPending, setIsDeactivationPending] = useState<boolean>(false);
  const Toaster = useToasts();

  const deactivatePrincipal = async () => {
    const { id: principalId } = principalToBeDeactivated!;
    setIsDeactivationPending(true);
    try {
      await updateUserPreference(principalId, { canBeAssigned: false, service: 'ECLOSE' });
      await refresh();
    } catch (err) {
      Sentry.captureException(err);
      Toaster?.addToast({
        message: 'Unable to switch status to inactive.',
        theme: 'error',
      });
    } finally {
      setIsDeactivationPending(false);
      setIsConfirmationDialogOpen(false);
    }
  };
  const openDialog = (cell: Report.StatusIndicatorDataCell) => () => {
    setPrincipalToBeDeactivated({ id: cell.principalId, name: cell.principalName });
    setIsConfirmationDialogOpen(true);
  };
  const closeDialog = () => setIsConfirmationDialogOpen(false);
  const deactivatePrincipalDialog = (
    <Modal contentLabel="Confirm status update" handleClose={closeDialog} isOpen={isConfirmationDialogOpen}>
      <ModalContent
        handleClose={closeDialog}
        headerText={`Are you sure you want to switch ${principalToBeDeactivated?.name}'s status to inactive?`}
      >
        <Text>
          This will auto-reassign all of their closings and you won't be able to undo it nor switch them back to active.
        </Text>
        <div className={css(styles.buttonRow)}>
          <ActionButton label="Cancel" onClick={closeDialog} theme="secondary" />
          <ActionButton
            isLoading={isDeactivationPending}
            label="Switch to inactive"
            onClick={deactivatePrincipal}
            theme="warning"
          />
        </div>
      </ModalContent>
    </Modal>
  );

  /**
   * Function that adds different styles based on the type of cell. Some cellTypes are
   * NUMBER, STATUS_INDICATOR, COLLAPSIBLE_TEXT
   * @param cell
   */
  const formatCell = (cell: Report.ReportDataCell): React.ReactNode => {
    switch (cell.type) {
      case 'NUMBER':
        return (
          <div className={css(styles.numberStyle)}>
            <Text colorName="NIGHT_60" fontSize="sm" weight="medium">
              {cell.value}
            </Text>
          </div>
        );
      case 'STATUS_INDICATOR':
        const active = cell.value === 'Active';
        const statusStyle = active ? styles.activeStatus : styles.inactiveStatus;
        return (
          <div className={css(styles.statusIndicator, statusStyle)}>
            <Text colorName="NIGHT_80" fontSize="md" weight="medium">
              {cell.value}
            </Text>
            {active && (
              <LoneIconButton
                btnSize="xs"
                className={css(styles.deactivateBtn)}
                icon="cloud_off"
                isCircle
                onClick={openDialog(cell)}
                theme="tertiary"
                title="Switch status to inactive"
              />
            )}
          </div>
        );
      case 'COLLAPSIBLE_TEXT':
        if (cell.value.length > maxAccordionTitleCharacterLength) {
          return <Accordion content={cell.value} truncateAfterLength={maxAccordionTitleCharacterLength} />;
        } else {
          return (
            <Text colorName="NIGHT_80" fontSize="md" weight="medium">
              {cell.value}
            </Text>
          );
        }
      default:
        return (
          <Text colorName="NIGHT_80" fontSize="md" weight="medium">
            {cell.value}
          </Text>
        );
    }
  };

  /**
   * Function to parse array of objects @{rows} in props and return styled rows
   */
  const renderRows = (): React.ReactNode[][] => {
    const { currentSortingColumn, sortOrder } = state;

    if (currentSortingColumn !== null) {
      rows = orderBy(rows, [row => row[currentSortingColumn].value], [sortOrder]);
    }
    return rows.map((row, i) => {
      const arrOfCells = headers.map(col => (
        <div id={`${col.id}-${i}`} key={`${col.id}-${i}-key`}>
          {formatCell(row[col.id])}
        </div>
      ));

      return arrOfCells;
    });
  };

  if (formattedRecords === null) {
    return null;
  }

  return (
    <>
      <div className={css(styles.lenderTableWrapper)}>
        <LenderTable
          columnWidths={columnWidths || undefined}
          headerComponents={formattedRecords.formattedHeaders}
          headerStyle={styles.headerStyle}
          id={id}
          rightAlignLastCol={false}
          rowStyle={rowStyle}
          sections={formattedRecords.formattedRows}
        />
      </div>
      {deactivatePrincipalDialog}
    </>
  );
};
